import { Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import cn from './cn.js';
import Button from './Button.js';

const sizes = {
  xs: 'sm:max-w-xs',
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
  '6xl': 'sm:max-w-6xl',
  '7xl': 'sm:max-w-7xl',
  full: 'sm:max-w-[min(96vw,1600px)]',
};

const iconTones = {
  brand: 'bg-indigo-50 text-indigo-600',
  danger: 'bg-red-50 text-red-600',
  warning: 'bg-amber-50 text-amber-600',
  success: 'bg-emerald-50 text-emerald-600',
  info: 'bg-sky-50 text-sky-600',
  neutral: 'bg-gray-100 text-gray-600',
};

/**
 * Modal: the single dialog surface for the platform.
 *
 * - `screen`: fills the viewport (used by the file workspace).
 * - `footer`: node rendered in the sticky footer (usually Buttons).
 * - `dismissible={false}`: ignore backdrop clicks / Escape (e.g. while uploading).
 * - Body scrolls; header and footer stay put.
 */
export default function Modal({
  open,
  onClose,
  title,
  description,
  icon: Icon,
  iconTone = 'brand',
  size = 'md',
  screen = false,
  children,
  footer,
  header,
  dismissible = true,
  hideClose = false,
  initialFocus,
  className,
  bodyClassName,
  padded = true,
  zIndex = 'z-[70]',
}) {
  const fallbackFocus = useRef(null);
  const handleClose = dismissible ? onClose : () => {};

  return (
    <Transition.Root show={!!open} as={Fragment}>
      <Dialog as="div" className={cn('relative', zIndex)} onClose={handleClose} initialFocus={initialFocus || fallbackFocus}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-[2px]" aria-hidden="true" />
        </Transition.Child>

        <div className={cn('fixed inset-0 overflow-y-auto', screen ? 'p-2 sm:p-4' : '')}>
          <div className={cn('flex min-h-full justify-center', screen ? 'items-stretch' : 'items-end p-4 sm:items-center sm:p-6')}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-150"
              enterFrom="opacity-0 translate-y-2 sm:translate-y-0 sm:scale-[0.98]"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-2 sm:translate-y-0 sm:scale-[0.98]"
            >
              <Dialog.Panel
                className={cn(
                  'relative flex w-full transform flex-col overflow-hidden rounded-2xl bg-white text-left shadow-overlay ring-1 ring-gray-900/5 transition-all',
                  screen ? 'h-[calc(100vh-1rem)] sm:h-[calc(100vh-2rem)] max-w-none' : cn('max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)]', sizes[size] || sizes.md),
                  className,
                )}
              >
                {(title || header || !hideClose) && (
                  <div className={cn('flex shrink-0 items-start gap-4 border-b border-gray-100 px-5 py-4 sm:px-6', header && 'py-3')}>
                    {header ? (
                      <div className="min-w-0 flex-1">{header}</div>
                    ) : (
                      <>
                        {Icon && (
                          <span className={cn('mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full', iconTones[iconTone] || iconTones.brand)}>
                            <Icon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        )}
                        <div className="min-w-0 flex-1">
                          {title && (
                            <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                              {title}
                            </Dialog.Title>
                          )}
                          {description && (
                            <Dialog.Description className="mt-1 text-sm text-gray-500">{description}</Dialog.Description>
                          )}
                        </div>
                      </>
                    )}
                    {!hideClose && (
                      <button
                        ref={initialFocus ? undefined : fallbackFocus}
                        type="button"
                        onClick={onClose}
                        className="-m-1.5 ml-auto inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                )}

                <div className={cn('min-h-0 flex-1', screen ? 'flex flex-col overflow-hidden' : 'overflow-y-auto', padded && !screen && 'px-5 py-4 sm:px-6', bodyClassName)}>
                  {children}
                </div>

                {footer && (
                  <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-end sm:px-6">
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

/**
 * ConfirmDialog: compact confirmation for destructive or important actions.
 * Focus starts on Cancel for `tone="danger"`, on Confirm otherwise.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  icon: Icon = ExclamationTriangleIcon,
  loading = false,
  children,
  size = 'md',
}) {
  const cancelRef = useRef(null);
  const confirmRef = useRef(null);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      icon={Icon}
      iconTone={tone === 'danger' ? 'danger' : tone === 'warning' ? 'warning' : 'brand'}
      size={size}
      hideClose
      dismissible={!loading}
      initialFocus={tone === 'danger' ? cancelRef : confirmRef}
      footer={
        <>
          <Button ref={cancelRef} variant="secondary" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            ref={confirmRef}
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
      padded={!!children}
    >
      {children}
    </Modal>
  );
}
